/**
 * useCalendarFilterPersistence
 *
 * Persists SP Calendar chip filters via:
 * - URL search params (?filterBy=... — refresh restores state)
 * - Backend view-preferences API (GET/PUT /user-management/user-preference/{viewName})
 *
 * Uses a committed vs staged pattern (same as useTableUrlPersistence):
 * - Chips write to the Zustand store immediately (staged / live).
 * - Only when the user clicks "Apply Filter" are the staged values committed,
 *   which triggers a provider list refetch and a backend preference save.
 * - "Reset Filter" clears both staged and committed state.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import type { TColumn } from "@/components/ui/TableCard/TableCard";
import { useGetViewPreferences, useSaveViewPreferences } from "@/hooks/useViewPreferences";
import { tableSearchSchema } from "@/hooks/useTableUrlPersistence";
import type { FilterValue } from "@/store/tableFilterStore";
import { useTableFilterBaseStore, useTableFilterStore } from "@/store/tableFilterStore";

export const spCalendarSearchSchema = tableSearchSchema.extend({
  tab: z.string().optional().catch(undefined),
  visitId: z.string().optional().catch(undefined),
});

export type SPCalendarSearchParams = z.infer<typeof spCalendarSearchSchema>;

type CalendarStoredFilterEntry = string | { id: string; value: string; label?: string };

function calendarEntryToFilterValue(entry: CalendarStoredFilterEntry): FilterValue {
  if (typeof entry === "string") {
    return { id: entry, value: entry, label: entry };
  }
  return {
    id: entry.id,
    value: entry.value,
    label: entry.label ?? entry.value,
  };
}

function restoreCalendarFiltersFromString(
  filterByJson: string,
  filterKeyToLabel: Record<string, string>,
  addFilter: (colLabel: string, value: FilterValue) => void,
) {
  try {
    const filterObj = JSON.parse(filterByJson) as Record<string, CalendarStoredFilterEntry[]>;
    Object.entries(filterObj).forEach(([filterByKey, entries]) => {
      const colLabel = filterKeyToLabel[filterByKey];
      if (!colLabel) return;
      entries.forEach((entry) => {
        addFilter(colLabel, calendarEntryToFilterValue(entry));
      });
    });
  } catch (error) {
    console.error("Error restoring calendar filters from string:", error);
  }
}

function parseFilterByToCommitted(
  filterByJson: string,
  filterKeyToLabel: Record<string, string>,
): Record<string, FilterValue[]> {
  try {
    const filterObj = JSON.parse(filterByJson) as Record<string, CalendarStoredFilterEntry[]>;
    const result: Record<string, FilterValue[]> = {};
    Object.entries(filterObj).forEach(([filterByKey, entries]) => {
      const colLabel = filterKeyToLabel[filterByKey];
      if (!colLabel) return;
      result[colLabel] = entries.map(calendarEntryToFilterValue);
    });
    return result;
  } catch {
    return {};
  }
}

function buildFilterByJson(
  committedFilters: Record<string, FilterValue[]>,
  filterColumns: TColumn[],
): string | undefined {
  const filterByKeyValues: Record<
    string,
    Array<{ id: string; value: string; label?: string }>
  > = {};

  for (const col of filterColumns) {
    const filterByKey = col.searchParams?.filterByKey;
    if (!filterByKey) continue;
    const colLabel = col.label;
    const values = committedFilters[colLabel] ?? [];
    for (const fv of values) {
      const entry: { id: string; value: string; label?: string } = {
        id: fv.id,
        value: fv.value,
      };
      if (fv.label != null && fv.label !== fv.value) {
        entry.label = fv.label;
      }
      const list = filterByKeyValues[filterByKey] ?? [];
      list.push(entry);
      filterByKeyValues[filterByKey] = list;
    }
  }

  return Object.keys(filterByKeyValues).length > 0 ? JSON.stringify(filterByKeyValues) : undefined;
}

export type CalendarFilterPersistenceNavigate = (opts: {
  search: (prev: Record<string, unknown>) => Record<string, unknown>;
  replace?: boolean;
}) => void;

export type DefaultFilterEntry = {
  filterByKey: string;
  colLabel: string;
  id: string;
  value: string;
  label: string;
};

export type UseCalendarFilterPersistenceOptions = {
  viewName: string;
  filterKeyToLabel: Record<string, string>;
  searchParams: SPCalendarSearchParams;
  navigate: CalendarFilterPersistenceNavigate;
  filterColumns: TColumn[];
  enableUrlSync?: boolean;
  defaultFilters?: DefaultFilterEntry[];
};

export type UseCalendarFilterPersistenceReturn = {
  viewName: string;
  searchValue: string;
  setSearchValue: React.Dispatch<React.SetStateAction<string>>;
  isPrefsLoading: boolean;
  isFilterHydrationDone: boolean;
  committedFilters: Record<string, FilterValue[]>;
  applyCalendarFilters: () => void;
  resetCalendarFilters: () => void;
};

export function useCalendarFilterPersistence({
  viewName,
  filterKeyToLabel,
  searchParams,
  navigate,
  filterColumns,
  enableUrlSync = true,
  defaultFilters,
}: UseCalendarFilterPersistenceOptions): UseCalendarFilterPersistenceReturn {
  const resetFiltersBase = useTableFilterBaseStore((s) => s.resetFilters);
  const addFilterBase = useTableFilterBaseStore((s) => s.addFilter);

  const { filters: liveFilters } = useTableFilterStore(viewName, "all");

  const [committedFilters, setCommittedFilters] = useState<Record<string, FilterValue[]>>({});

  const [searchValue, setSearchValue] = useState(() =>
    enableUrlSync ? (searchParams.search ?? "") : "",
  );

  const [isFilterHydrationDone, setIsFilterHydrationDone] = useState(false);

  const { mutate: savePreferences } = useSaveViewPreferences(viewName);

  const applyDefaultFilters = useCallback(() => {
    if (!defaultFilters?.length) return;
    const committed: Record<string, FilterValue[]> = {};
    for (const df of defaultFilters) {
      const fv: FilterValue = { id: df.id, value: df.value, label: df.label };
      addFilterBase(viewName, df.colLabel, fv);
      const list = committed[df.colLabel] ?? [];
      list.push(fv);
      committed[df.colLabel] = list;
    }
    setCommittedFilters(committed);
  }, [addFilterBase, defaultFilters, viewName]);

  // Step 1: On mount, reset store and restore from URL (synchronous, before paint)
  useLayoutEffect(() => {
    resetFiltersBase(viewName);
    if (enableUrlSync && searchParams.filterBy) {
      const add = (colLabel: string, value: FilterValue) =>
        addFilterBase(viewName, colLabel, value);
      restoreCalendarFiltersFromString(searchParams.filterBy, filterKeyToLabel, add);
      setCommittedFilters(parseFilterByToCommitted(searchParams.filterBy, filterKeyToLabel));
    } else {
      applyDefaultFilters();
    }
  }, []);

  // Step 2: Fetch backend prefs; if available, override URL state
  const {
    data: viewPreferences,
    isSuccess: prefsLoaded,
    isLoading: isGetPrefsLoading,
    isError: isPrefsError,
  } = useGetViewPreferences({ viewName, enabled: true });

  const isPrefsReady = prefsLoaded || isPrefsError;
  const hasRestoredFromPrefsRef = useRef(false);

  useEffect(() => {
    if (!isPrefsReady) return;
    if (hasRestoredFromPrefsRef.current) return;
    hasRestoredFromPrefsRef.current = true;

    const qs = viewPreferences?.queryString;
    const hasRealPrefs = qs && qs !== "v=1";

    if (hasRealPrefs) {
      const params = new URLSearchParams(qs);
      const savedFilterBy = params.get("filterBy");
      const savedSearch = params.get("search") ?? "";

      resetFiltersBase(viewName);

      if (savedFilterBy) {
        const add = (colLabel: string, value: FilterValue) =>
          addFilterBase(viewName, colLabel, value);
        restoreCalendarFiltersFromString(savedFilterBy, filterKeyToLabel, add);
        setCommittedFilters(parseFilterByToCommitted(savedFilterBy, filterKeyToLabel));
      } else {
        applyDefaultFilters();
      }

      if (savedSearch) setSearchValue(savedSearch);
    } else if (!(enableUrlSync && searchParams.filterBy)) {
      resetFiltersBase(viewName);
      applyDefaultFilters();
    }

    setIsFilterHydrationDone(true);
  }, [
    isPrefsReady,
    viewPreferences?.queryString,
    viewName,
    filterKeyToLabel,
    addFilterBase,
    resetFiltersBase,
    applyDefaultFilters,
    enableUrlSync,
    searchParams.filterBy,
  ]);

  // Step 3: Keep URL in sync with committed filters
  const urlFilterBy = useMemo(
    () => buildFilterByJson(committedFilters, filterColumns),
    [committedFilters, filterColumns],
  );
  const urlSyncKey = JSON.stringify({ filterBy: urlFilterBy, search: searchValue || undefined });

  useEffect(() => {
    if (!enableUrlSync || !isFilterHydrationDone) return;

    navigate({
      search: (prev: Record<string, unknown>) => {
        const next = { ...prev };
        if (urlFilterBy) {
          next.filterBy = urlFilterBy;
        } else {
          delete next.filterBy;
        }
        if (searchValue) {
          next.search = searchValue;
        } else {
          delete next.search;
        }
        return next;
      },
      replace: true,
    });
  }, [enableUrlSync, isFilterHydrationDone, urlSyncKey, navigate]);

  // Apply: snapshot staged chip values → committed → save to backend
  const applyCalendarFilters = useCallback(() => {
    const snapshot: Record<string, FilterValue[]> = {};
    Object.entries(liveFilters).forEach(([key, values]) => {
      if (values.length > 0) snapshot[key] = values;
    });
    setCommittedFilters(snapshot);

    const filterBy = buildFilterByJson(snapshot, filterColumns);
    const qs = filterBy ? `filterBy=${encodeURIComponent(filterBy)}` : "v=1";
    savePreferences({ queryString: qs });
  }, [liveFilters, filterColumns, savePreferences]);

  // Reset: clear everything
  const resetCalendarFilters = useCallback(() => {
    resetFiltersBase(viewName);
    setCommittedFilters({});
    setSearchValue("");
    savePreferences({ queryString: "v=1" });
    if (enableUrlSync) {
      navigate({
        search: (prev: Record<string, unknown>) => {
          const next = { ...prev };
          delete next.filterBy;
          delete next.search;
          return next;
        },
        replace: true,
      });
    }
  }, [enableUrlSync, navigate, resetFiltersBase, savePreferences, viewName]);

  return {
    viewName,
    searchValue,
    setSearchValue,
    isPrefsLoading: isGetPrefsLoading,
    isFilterHydrationDone,
    committedFilters,
    applyCalendarFilters,
    resetCalendarFilters,
  };
}
